import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SearchStore } from '@shared/components/organisms/search-bar/store/search.store';
import {
  FilterSupplement,
  PublicDocket,
  SearchSuggestion,
  SuggestionItem,
  SuggestionType,
} from '@shared/components/organisms/search-bar/models/search.model';
import { Router } from '@angular/router';
import { MatListItem, MatNavList } from '@angular/material/list';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ButtonComponent } from '@shared/components/atoms/button/button.component';
import { NMFTAIconComponent } from '@shared/components/atoms/icon/icon.component';
import { debounceTime, Subscription } from 'rxjs';
import { NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { navigateToSearch } from '@shared/utils/routingUtils';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '@shared/components/organisms/dialog/dialog.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { formatMatDatepickerDate } from '@shared/utils/date.utils';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { InputWrapperComponent } from '@shared/components/molecules/input-wrapper/input-wrapper.component';
import { NmftaDateMaskDirective } from '@shared/directives/date-mask.directive';
import { UserStore } from '@shared/stores/user.store';

@Component({
  selector: 'nmfta-search-bar',
  imports: [
    MatInput,
    ReactiveFormsModule,
    MatListItem,
    MatNavList,
    NgxSkeletonLoaderModule,
    ButtonComponent,
    NMFTAIconComponent,
    NgTemplateOutlet,
    MatSuffix,
    TranslatePipe,
    InputWrapperComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelect,
    MatOption,
    TitleCasePipe,
    NmftaDateMaskDirective,
  ],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBarComponent implements OnInit, OnDestroy, OnChanges {

  private readonly userStore = inject(UserStore);
  protected readonly isInternalUser$: Signal<boolean> = this.userStore.isInternalUser;

  @Input() searchInProgress = false;
  @Input() displayCompactMode = false;
  @Input() searchBarHasFocus = false;
  @Input() previousSearchText: string | null = null;

  @Output() searchPerformed: EventEmitter<string> = new EventEmitter<string>();
  @Output() suggestionSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() searchBarFocusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('searchInstructions') searchInstructions!: TemplateRef<ElementRef>;
  @ViewChild('input') searchInputRef!: ElementRef;

  searchStore = inject(SearchStore);
  router = inject(Router);
  dialog = inject(MatDialog);
  translate: TranslateService = inject(TranslateService);

  searchForm: FormGroup;
  // Make this dynamic based on internal or external user
  // Calculate a date that is 10 years in the past from today
  // Uses an IIFE (Immediately Invoked Function Expression) to execute once during class initialization
  tenYearsFromToday = (() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 10);
    return date;
  })();

  // Set minimum date based on user type:
  // - Internal users can access data back to January 1, 2007
  // - External users are restricted to the last 10 years only
  minDate = this.isInternalUser$() ? new Date(2007, 0, 1) : this.tenYearsFromToday;

  // Set maximum date to today - prevents selection of future dates
  maxDate = new Date();

  suggestions$: Signal<SearchSuggestion> = this.searchStore.uiSuggestions;
  searchSubscription$: Subscription;

  displayingFilters = false;
  activeFilterName: string | null = null;
  supplementFilterOptions$: Signal<FilterSupplement[]> = this.searchStore.uiSupplement;
  docketFilterOptions$: Signal<PublicDocket[]> = this.searchStore.uiDocket;

  private readonly MIN_SEARCH_LENGTH = 2;
  private readonly MIN_SUGGESTION_LENGTH = 3;
  private readonly resultMap: Record<SuggestionType, string> = {
    [SuggestionType.Other]: 'Other',
    [SuggestionType.Keyword]: 'Keyword',
    [SuggestionType.History]: 'History',
    [SuggestionType.Heading]: 'Heading',
    [SuggestionType.Synonym]: 'Synonym',
    [SuggestionType.Note]: 'Note',
  };

  constructor(private fb: FormBuilder) {
    this.searchForm = this.fb.group({
      search: this.fb.control(''),
      contentDate: this.fb.control(''),
      supplementDateFrom: this.fb.control(''),
      supplementDateTo: this.fb.control(''),
      supplement: this.fb.control(''),
      publicDocket: this.fb.control(''),
    });

    this.searchBarFocusChanged.subscribe((hasFocus: boolean) => {
      if (hasFocus) {
        setTimeout(() => {
          this.searchInputRef.nativeElement.focus();
        }, 50)
      }
    })
  }

  ngOnInit(): void {
    this.searchStore.clear();
    this.searchStore.loadFilterOptions();

    if (this.searchBarHasFocus) {
      this.enterSearch();
    }
    this.bindEvents();
    //Prepopulate the search input with the previous search text if available
    const searchControl = this.searchForm.get('search');
    const searchFilters = this.searchStore.searchFilters();

    if (searchControl && this.previousSearchText) {
      searchControl.patchValue(this.previousSearchText);

      if (
        searchFilters.contentDate ||
        searchFilters.supplementDateFrom ||
        searchFilters.supplementDateTo ||
        searchFilters.supplement ||
        searchFilters.docket
      ) {
        this.searchForm.patchValue({
          contentDate: this.parseLocalDate(searchFilters.contentDate),
          supplementDateFrom: this.parseLocalDate(searchFilters.supplementDateFrom),
          supplementDateTo: this.parseLocalDate(searchFilters.supplementDateTo),
          supplement: searchFilters.supplement || '',
          publicDocket: searchFilters.docket || '',
        });

        this.displayingFilters = true;
      } else {
        this.searchStore.clearSearchFilters();
      }
    }

    this.setActiveInput('contentDate');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchBarHasFocus']) {
      if (this.searchBarHasFocus) {
        this.searchBarFocusChanged.emit(true);
      }
    }
  }

  parseLocalDate(dateStr?: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if ([year, month, day].some(Number.isNaN)) return null;
    return new Date(year, month - 1, day);
  }

  ngOnDestroy() {
    this.searchStore.clear();
    this.searchBarFocusChanged.emit(false);

    if (this.searchSubscription$) {
      this.searchSubscription$.unsubscribe();
    }
  }

  bindEvents() {
    const searchControl = this.searchForm.get<string>('search');

    if (searchControl?.valueChanges) {
      this.searchSubscription$ = searchControl.valueChanges.pipe(debounceTime(50)).subscribe((val) => {
        // Do not load suggestions when the filters are opened
        if (!this.displayingFilters) {
          if (val?.length >= this.MIN_SUGGESTION_LENGTH) {
            this.searchStore.loadSuggestions(val);
            return;
          }

          if (val?.length === 0) {
            this.searchStore.clear();
          }
        }
      });
    }
  }

  handleSearch() {
    if (this.validForm()) {
      const queryParams: Record<string, string> = this.formValuesToQueryParams();

      if (this.searchPerformed.observed) {
        this.searchPerformed.emit(queryParams['searchText']);
      } else {
        this.leaveSearch();
        this.selectSearchPage(queryParams);
      }
    }
  }

  async selectSearchPage(queryParams: Record<string, any>): Promise<void> {
    // Directly navigate to detailed search when the searchText is less than 3-chars long
    if (queryParams['searchText']?.length < 3) {
      this.router.navigate(['home', 'detailed-search'], { queryParams });
    }

    // Wait until suggestions has data
    while (this.searchStore.suggestionsStatus() != ResponseStatus.LOADED) {
      await new Promise((resolve) => setTimeout(resolve, 50)); // Poll every 50ms
    }

    // Once suggestions are loaded, navigate
    const suggestionsObj = this.suggestions$();
    // If there are more than 5 suggestions or any filters are applied, navigate to detailed search
    // If there are no suggestions, also navigate to detailed search
    if (
      (suggestionsObj.suggestion.length > 5 && !suggestionsObj.isNumber) ||
      suggestionsObj.suggestion.length === 0 ||
      // check for undefined and empty string to see if any filters are applied
      (queryParams['publicDocket'] != undefined && queryParams['publicDocket'] != '') ||
      (queryParams['supplement'] != undefined && queryParams['supplement'] != '') ||
      (queryParams['contentDate'] != undefined && queryParams['contentDate'] != '') ||
      (queryParams['supplementDateFrom'] != undefined && queryParams['supplementDateFrom'] != '') ||
      (queryParams['supplementDateTo'] != undefined && queryParams['supplementDateTo'] != '')
    ) {
      this.searchStore.clearSearchFilters();
      this.searchStore.setSearchFilters({
        searchTerm: queryParams['searchText'],
        contentDate: queryParams['contentDate'],
        supplementDateFrom: queryParams['supplementDateFrom'],
        supplementDateTo: queryParams['supplementDateTo'],
        supplement: queryParams['supplement'],
        docket: queryParams['publicDocket'],
      });
      this.router.navigate(['home', 'detailed-search'], { queryParams });
    } else {
      if (suggestionsObj.isNumber) {
        queryParams['itemNumber'] = queryParams['searchText'];
        queryParams['searchText'] = undefined; // Remove searchText if itemNumber is present
      }
      this.router.navigate(['home', 'default-search'], {
        queryParams,
      });
    }
  }

  private formValuesToQueryParams(): Record<string, any> {
    const queryParams: Record<string, any> = {};

    queryParams['searchText'] = this.searchForm.get<string>('search')?.value;
    queryParams['page'] = 0;
    queryParams['pageSize'] = 50;

    //When NMFC filters are added check form values and set activeFilterName
    if (this.displayingFilters) {
      // set the active filter name depending on which filter has a value
      if (this.searchForm.get('contentDate')?.value) {
        this.activeFilterName = 'contentDate';
      } else if (this.searchForm.get('supplement')?.value) {
        this.activeFilterName = 'supplement';
      } else if (this.searchForm.get('publicDocket')?.value) {
        this.activeFilterName = 'publicDocket';
      } else if (this.searchForm.get('supplementDateFrom')?.value || this.searchForm.get('supplementDateTo')?.value) {
        this.activeFilterName = 'supplementDateRange';
      }
    }

    switch (this.activeFilterName) {
      case 'contentDate':
        queryParams['contentDate'] = formatMatDatepickerDate(this.searchForm.get('contentDate')?.value);
        break;
      case 'supplementDateRange':
        queryParams['supplementDateFrom'] = formatMatDatepickerDate(this.searchForm.get('supplementDateFrom')?.value);
        queryParams['supplementDateTo'] = formatMatDatepickerDate(this.searchForm.get('supplementDateTo')?.value);
        break;
      case 'supplement':
        queryParams['supplement'] = this.searchForm.get('supplement')?.value;
        break;
      case 'publicDocket':
        queryParams['publicDocket'] = this.searchForm.get('publicDocket')?.value;
        break;
    }

    return queryParams;
  }

  handleDetailedSearch(searchText: string, $event: Event) {
    $event.preventDefault();
    this.leaveSearch();

    this.router.navigate(['/home', 'detailed-search'], {
      queryParams: { searchText },
    });
  }

  handleSuggestionClick(suggestion: SuggestionItem, $event: Event) {
    $event.stopPropagation();
    $event.preventDefault();

    if (this.suggestionSelected.observed) {
      this.suggestionSelected.emit(suggestion.itemNumber);
    } else {
      this.leaveSearch();
      navigateToSearch(this.router, '', suggestion.itemNumber);
    }
  }

  enterSearch() {
    const scrollContainer = document.querySelector('.root-container');
    scrollContainer?.scrollTo(0, 0);
    if (!this.searchBarHasFocus) {
      this.searchBarHasFocus = true;
      document.body.classList.add('backdrop-active');

      this.searchBarFocusChanged.emit(true);
    }
  }

  leaveSearch() {
    setTimeout(() => {
      this.searchBarHasFocus = false;
      this.displayingFilters = false;
      this.resetFilters();
    }, 0);
    this.searchBarFocusChanged.emit(false);

    document.body.classList.remove('backdrop-active');
  }

  handleOpenInstructions() {
    const instructionsDialogRef = this.dialog.open(DialogComponent, {
      width: '660px',
      height: 'auto',
      data: {
        title: 'SEARCH.INSTRUCTIONS_TITLE',
        icon: 'info',
        content: this.searchInstructions,
        actions: [
          {
            id: 'close',
            label: 'Got It!',
            styleType: 'contained',
            onClick: (): void => instructionsDialogRef.close(),
          },
        ],
      },
    });
  }

  validForm(): boolean {
    const validInput = this.searchForm.get('search')?.value?.length >= this.MIN_SEARCH_LENGTH;

    if (!this.displayingFilters) {
      return validInput;
    }

    return validInput && !this.searchForm.invalid;
  }

  toggleFilters() {
    this.displayingFilters = !this.displayingFilters;

    if (this.displayingFilters) {
      this.activeFilterName = 'contentDate';
    } else {
      // Reset filters and validation when closing
      this.resetFilters();
    }
  }

  private resetFilters() {
    // Clear filters and keep the search terms
    const searchTerms = this.searchForm.get('search')?.value;

    this.searchForm.reset();
    this.searchForm.get('search')?.setValue(searchTerms);
    this.activeFilterName = null;
  }

  private setControlRequired(controlName: string, required: boolean) {
    const control = this.searchForm.get(controlName);
    if (!control) return;
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({ onlySelf: true });
  }

  setActiveInput(filterControlName: string) {
    if (this.activeFilterName === filterControlName) {
      return;
    }

    this.activeFilterName = filterControlName;

    const controlsToManage = [
      { name: 'contentDate', filterName: 'contentDate' },
      { name: 'supplementDateFrom', filterName: 'supplementDateRange' },
      { name: 'supplementDateTo', filterName: 'supplementDateRange' },
      { name: 'supplement', filterName: 'supplement' },
      { name: 'publicDocket', filterName: 'publicDocket' },
    ];

    if (this.activeFilterName === 'supplement') {
      const control = this.searchForm.get('supplement');
      const valueToSet = this.supplementFilterOptions$()[0]?.id ?? null;
      control?.setValue(valueToSet);
    } else if (this.activeFilterName === 'publicDocket') {
      const control = this.searchForm.get('publicDocket');
      const valueToSet = this.docketFilterOptions$()[0]?.id ?? null;
      control?.setValue(valueToSet);
    }

    // clear values/touched on inactive controls
    controlsToManage.forEach(({ name, filterName }) => {
      const control = this.searchForm.get(name);
      if (control && this.activeFilterName !== filterName) {
        control.setValue('');
        control.markAsUntouched();
      }
    });

    // make only the active filter required
    ['contentDate', 'supplementDateFrom', 'supplementDateTo', 'supplement', 'publicDocket']
      .forEach((n) => this.setControlRequired(n, false));

    switch (this.activeFilterName) {
      case 'contentDate':
        this.setControlRequired('contentDate', true);
        break;
      case 'supplementDateRange':
        this.setControlRequired('supplementDateFrom', true);
        this.setControlRequired('supplementDateTo', true);
        break;
      case 'supplement':
        this.setControlRequired('supplement', true);
        break;
      case 'publicDocket':
        this.setControlRequired('publicDocket', true);
        break;
    }

    this.searchForm.updateValueAndValidity();
  }


  getSuggestionTypeText(value: SuggestionType) {
    return this.resultMap[value] ?? value;
  }
}
